import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { IonInput, ModalController } from '@ionic/angular';
import { Articolo } from '../../../_models/Articolo';
import { SearchResult } from '../../../_models/SearchResult';
import { ArticoliService } from '../../../_services/articoli.service';
import { OrganizzazioniService } from '../../../_services/organizzazioni.service';
import { SaveMessage } from '../../../_helpers/save-message';
import { MessageConfirm } from '../../../_helpers/message-confirm.service';
import { ArticoliProdottiCreateComponent, emptyArticolo } from '../../articoli-prodotto/articoli-prodotti-create/articoli-prodotti-create.component';

@Component({
  selector: 'app-select-articolo-prodotto',
  templateUrl: './select-articolo-prodotto.component.html',
  standalone: false
})
export class SelectArticoloProdottoComponent implements OnInit, OnDestroy {
  @Input() articoloId = 0;
  @Output() articoloIdChange = new EventEmitter<number>();
  @Output() articoloChange = new EventEmitter<Articolo>();
  @Output() codArtLibero = new EventEmitter<string>();
  @Input() label = 'Articolo';
  // Codice articolo iniziale dell'entità ospite (es. ordine/materiale in modifica): con
  // articoloId = 0 inizializza lo stato "valore libero" senza emettere nulla.
  @Input() codArt = '';

  @ViewChild('searchInput') searchInput?: IonInput;

  articoloSelezionato: Articolo | null = null;
  search = '';
  risultati: Articolo[] = [];
  showDropdown = false;
  loading = false;
  valoreLibero = '';

  private commitInCorso = false;
  private blurTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private articoliService: ArticoliService,
    private organizzazioniService: OrganizzazioniService,
    private modalCtrl: ModalController,
    private saveMessage: SaveMessage,
    private messageConfirm: MessageConfirm
  ) {}

  ngOnInit() {
    if (this.articoloId > 0) {
      this.caricaArticoloSelezionato();
    } else if (this.codArt) {
      this.valoreLibero = this.codArt;
    }
  }

  ngOnDestroy() {
    this.annullaCommitPendente();
  }

  caricaArticoloSelezionato() {
    this.loading = true;
    this.articoliService
      .getArticoloAsync(this.organizzazioniService.organizzazioneId, this.articoloId)
      .then(a => (this.articoloSelezionato = a))
      .catch(() => {
        this.saveMessage.messageError('Errore nel caricamento dell\'articolo.');
        // Fallback (US4): se l'entità ospite ha un codice salvato, mostralo come valore
        // libero (puramente visivo, senza emettere eventi che marcherebbero una modifica).
        if (this.codArt) this.valoreLibero = this.codArt;
      })
      .finally(() => (this.loading = false));
  }

  private eseguiRicerca(testo: string): Promise<Articolo[]> {
    return this.articoliService
      .getArticoliFilteredAsync(
        this.organizzazioniService.organizzazioneId,
        testo, 0, 20, 'codArt', true
      )
      .then((result: SearchResult<Articolo>) => result?.result ?? []);
  }

  onEnter() {
    this.annullaCommitPendente();
    this.commitRicerca();
  }

  onBlur() {
    this.annullaCommitPendente();
    this.blurTimeout = setTimeout(() => this.commitUscita(), 250);
  }

  private annullaCommitPendente() {
    if (this.blurTimeout !== null) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
  }

  async commitRicerca() {
    const testo = this.search.trim();
    if (this.commitInCorso || !testo) {
      return;
    }
    this.commitInCorso = true;
    this.loading = true;
    try {
      const risultati = await this.eseguiRicerca(testo);
      if (this.search.trim() !== testo) {
        return;
      }
      // commitInCorso resta true anche durante alert/modale (await): il blur
      // rubato dall'alert non può innescare un secondo commit (una sola richiesta).
      await this.gestisciEsito(testo, risultati);
    } catch {
      this.saveMessage.messageError('Errore nella ricerca articoli.');
    } finally {
      this.loading = false;
      this.commitInCorso = false;
    }
  }

  // Uscita dal campo: nessuna ricerca e nessun alert. Il testo digitato e non
  // selezionato dai risultati resta come valore libero; se coincide col valore
  // corrente (selezione o valore libero, case-insensitive) non cambia nulla.
  private commitUscita() {
    if (this.commitInCorso) {
      return;
    }
    const testo = this.search.trim();
    if (!testo || this.coincideConSelezione(testo) || this.coincideConValoreLibero(testo)) {
      this.ripristinaVistaCorrente();
      return;
    }
    this.impostaValoreLibero(testo);
  }

  private coincideConSelezione(testo: string): boolean {
    return !!this.articoloSelezionato &&
      testo.toLowerCase() === (this.articoloSelezionato.codArt ?? '').toLowerCase();
  }

  // Il backend cerca case-insensitive: un ritocco di sole maiuscole/minuscole
  // non deve essere trattato come testo nuovo (alert già rifiutato).
  private coincideConValoreLibero(testo: string): boolean {
    return !!this.valoreLibero && testo.toLowerCase() === this.valoreLibero.toLowerCase();
  }

  // Nessuna autoselezione: i risultati (anche uno solo) vanno sempre nel dropdown
  // e l'operatore sceglie col click; se non sceglie, all'uscita resta valore libero.
  private async gestisciEsito(testo: string, risultati: Articolo[]) {
    if (risultati.length > 0) {
      this.risultati = risultati;
      this.showDropdown = true;
      return;
    }
    await this.chiediCreazione(testo);
  }

  private async chiediCreazione(testo: string) {
    const crea = await this.messageConfirm.presentCustomAlert(
      'Conferma',
      `Nessun articolo trovato per «${testo}». Creare un nuovo articolo?`,
      'Sì', 'success', () => true
    );
    if (crea) {
      await this.apriNuovoArticolo(testo);
    } else {
      this.impostaValoreLibero(testo);
    }
  }

  private ripristinaVistaCorrente() {
    this.showDropdown = false;
    this.risultati = [];
  }

  private impostaValoreLibero(testo: string) {
    this.valoreLibero = testo;
    this.articoloSelezionato = null;
    this.articoloId = 0;
    this.search = '';
    this.risultati = [];
    this.showDropdown = false;
    this.articoloIdChange.emit(0);
    this.codArtLibero.emit(testo);
  }

  // Riapertura del campo: mantiene visibile il valore corrente (codice dell'articolo
  // selezionato o valore libero), pronto per essere modificato, con focus programmatico.
  riapriRicerca() {
    this.annullaCommitPendente();
    this.showDropdown = true;
    this.search = this.articoloSelezionato?.codArt ?? this.valoreLibero;
    setTimeout(() => this.searchInput?.setFocus());
  }

  seleziona(articolo: Articolo) {
    this.annullaCommitPendente();
    this.valoreLibero = '';
    this.articoloSelezionato = articolo;
    this.articoloId = articolo.id;
    this.showDropdown = false;
    this.risultati = [];
    this.search = '';
    this.articoloIdChange.emit(articolo.id);
    this.articoloChange.emit(articolo);
  }

  svuota() {
    this.annullaCommitPendente();
    this.valoreLibero = '';
    this.articoloSelezionato = null;
    this.articoloId = 0;
    this.search = '';
    this.showDropdown = false;
    this.risultati = [];
    this.articoloIdChange.emit(0);
    this.articoloChange.emit(null);
  }

  async apriNuovoArticolo(codArtPrecompilato?: string) {
    this.annullaCommitPendente();
    const componentProps: { isNew: boolean; articolo?: Articolo } = { isNew: true };
    if (codArtPrecompilato) {
      const a = emptyArticolo();
      a.organizzazioneId = this.organizzazioniService.organizzazioneId;
      a.codArt = codArtPrecompilato;
      componentProps.articolo = a;
    }
    const modal = await this.modalCtrl.create({
      component: ArticoliProdottiCreateComponent,
      componentProps,
      backdropDismiss: false,
      cssClass: ['custom-modal', 'form-modal']
    });
    await modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm' && data) {
      this.seleziona(data as Articolo);
    }
  }
}
